import { useEffect, useRef, useState } from 'react';
import { MapPin, Star } from 'lucide-react';
import { cn } from '@/lib/cn';
import { t, countryName } from '@/i18n';
import { usePlaceSuggestions } from '@/api/hooks/catalog';
import type { Place } from '@/api/types';
import { Input } from '@/components/ui/Field';

/**
 * Подсказка за адрес. Запазените места излизат първи и с звезда — при ~52
 * товара месечно над повтарящ се набор адреси закаченото веднъж място бие
 * всеки геокодер завинаги (и носи бележката за шофьора и времето за товарене).
 */
export function PlaceAutocomplete({
  value,
  onChange,
  onSelect,
  country,
  placeholder,
  id,
}: {
  value: string;
  onChange: (value: string) => void;
  onSelect: (place: Place) => void;
  country?: string;
  placeholder?: string;
  id?: string;
}) {
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState(value);
  const [debounced, setDebounced] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => setTerm(value), [value]);

  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(term), 200);
    return () => window.clearTimeout(id);
  }, [term]);

  useEffect(() => {
    const onClickOutside = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const { data } = usePlaceSuggestions(debounced, country);
  const suggestions = data ?? [];

  return (
    <div ref={containerRef} className="relative">
      <Input
        id={id}
        value={term}
        placeholder={placeholder ?? t.places.search}
        autoComplete="off"
        onChange={(event) => {
          setTerm(event.target.value);
          onChange(event.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
      />
      {open && suggestions.length > 0 ? (
        <ul className="absolute z-30 mt-1 max-h-64 w-full overflow-y-auto rounded-md border border-line-strong bg-overlay shadow-xl">
          {suggestions.map((place) => (
            <li key={place.id}>
              <button
                type="button"
                className="flex w-full items-start gap-2 px-2.5 py-2 text-left hover:bg-raised"
                onClick={() => {
                  onSelect(place);
                  setTerm(place.name);
                  setOpen(false);
                }}
              >
                {place.usageCount > 0 ? (
                  <Star className="mt-0.5 size-3.5 shrink-0 text-accent" />
                ) : (
                  <MapPin className="mt-0.5 size-3.5 shrink-0 text-ink-faint" />
                )}
                <span className="min-w-0">
                  <span className="block truncate text-sm">{place.name}</span>
                  <span className="block truncate text-xs text-ink-dim">
                    {place.address}, {place.city}, {countryName(place.country)}
                    {place.usageCount > 0 ? ` · ${place.usageCount} пъти` : ''}
                  </span>
                  {place.driverNotes ? (
                    <span className={cn('block truncate text-[11px] text-ink-faint')}>
                      {place.driverNotes}
                    </span>
                  ) : null}
                </span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
